import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/format";
import { Field } from "@/components/common/kit";

export function PayButtons({ d, onPick }) {
  if (!(d.balance > 0)) return null;
  return <div className="flex gap-1"><Button size="sm" variant="ghost" onClick={() => onPick({ due: d, settle: false })} data-testid={`pay-record-${d.ref_no}`}>Record Payment</Button>
    <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => onPick({ due: d, settle: true })} data-testid={`pay-settle-${d.ref_no}`}>Settle Outstanding</Button></div>;
}

export function DirectPayDialog({ pick, onClose, onDone }) {
  const { bizDate, user } = useAuth();
  const d = pick?.due;
  const [f, setF] = useState(null);
  const v = f || { amount: pick?.settle ? String(d?.balance) : "", method: "cash", date: bizDate, reference: "", received_by: user?.name || user?.username || "", remarks: "" };
  const [key] = useState(() => crypto.randomUUID());
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const close = () => { setF(null); setConfirm(false); onClose(); };
  const save = async () => {
    setBusy(true);
    try { const r = await api.post("/credit/direct-payment", { due_id: d.id, ...v, amount: Number(v.amount), client_key: key + d.id + v.amount });
      toast.success(`Payment recorded. ${d.ref_no} balance ${inr(r.data.balance)} (${r.data.status})`); close(); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  const s = (k) => (e) => setF({ ...v, [k]: e.target.value });
  const amt = Number(v.amount) || 0;
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg" data-testid="direct-pay-dialog">
        <DialogHeader><DialogTitle>{pick.settle ? "Settle Outstanding" : "Record Payment"} · {d.ref_no}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2 rounded border bg-slate-50 p-3 text-sm">
          <p>Customer<br /><b data-testid="dp-customer">{d.customer_name}</b></p><p>Bill type<br /><b>{d.type === "WHOLESALE" ? "Wholesale" : "Retail / FMCG"}</b></p>
          <p>Original amount<br /><b className="font-mono">{inr(d.amount)}</b></p><p>Current outstanding<br /><b className="font-mono text-red-700" data-testid="dp-outstanding">{inr(d.balance)}</b></p>
        </div>
        {!confirm ? <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Amount received ₹"><Input type="number" value={v.amount} onChange={s("amount")} className="font-mono" data-testid="dp-amount" /></Field>
          <Field label="Payment method"><select className="h-10 w-full rounded border px-2 text-sm" value={v.method} onChange={s("method")} data-testid="dp-method"><option value="cash">Cash (office)</option><option value="upi">UPI / GPay</option><option value="bank">Bank</option></select></Field>
          <Field label="Payment date"><Input type="date" value={v.date} onChange={s("date")} data-testid="dp-date" /></Field>
          <Field label="Received by"><Input value={v.received_by} onChange={s("received_by")} data-testid="dp-received-by" /></Field>
          <Field label="Transaction reference"><Input value={v.reference} onChange={s("reference")} data-testid="dp-reference" /></Field>
          <Field label="Remarks"><Input value={v.remarks} onChange={s("remarks")} data-testid="dp-remarks" /></Field>
          {amt > d.balance + 0.001 && <p className="text-xs text-red-600 sm:col-span-2" data-testid="dp-error">Amount cannot exceed the outstanding {inr(d.balance)}.</p>}
        </div> : <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="dp-confirm-box">
          Confirm {inr(amt)} by <b>{v.method.toUpperCase()}</b> from <b>{d.customer_name}</b> against {d.ref_no}. Remaining after payment: <b className="font-mono">{inr(d.balance - amt)}</b>{d.balance - amt <= 0.001 && " — bill will be marked PAID"}.
        </div>}
        <DialogFooter>{!confirm ? <Button disabled={amt <= 0 || amt > d.balance + 0.001} onClick={() => setConfirm(true)} data-testid="dp-next">Continue</Button>
          : <><Button variant="ghost" onClick={() => setConfirm(false)}>Back</Button><Button disabled={busy} onClick={save} data-testid="dp-confirm">Confirm & save</Button></>}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
